import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../../../components/ui/card';
import { DataTable, DataTableColumn } from '../../../components/ui/data-table';
import { Button } from '../../../components/ui/button';

import { TrendingUp, Calendar, Download, Filter } from 'lucide-react';
import PageHeader from '../../../components/common/PageHeader';
import PerformanceCard from '../../../components/trading/PerformanceCard';
import ROIWidget from '../../../components/trading/ROIWidget';

type PerformanceData = {
  id: string;
  traderName: string;
  period: string;
  roi: number;
  totalProfit: number;
  totalTrades: number;
  winRate: number;
  maxDrawdown: number;
  sharpeRatio: number;
};

const CopyTradingPerformance = () => {
  const performanceData: PerformanceData[] = [
    {
      id: 'CPERF001',
      traderName: 'John Smith',
      period: 'January 2024',
      roi: 42.5,
      totalProfit: 53125,
      totalTrades: 289,
      winRate: 68.5,
      maxDrawdown: -12.3,
      sharpeRatio: 2.6,
    },
    {
      id: 'CPERF002',
      traderName: 'Jane Doe',
      period: 'January 2024',
      roi: 35.8,
      totalProfit: 35084,
      totalTrades: 234,
      winRate: 72.1,
      maxDrawdown: -9.7,
      sharpeRatio: 2.4,
    },
    {
      id: 'CPERF003',
      traderName: 'Bob Johnson',
      period: 'January 2024',
      roi: 18.2,
      totalProfit: 13650,
      totalTrades: 145,
      winRate: 62.2,
      maxDrawdown: -7.5,
      sharpeRatio: 2.0,
    },
  ];

  const columns: DataTableColumn<PerformanceData>[] = [
    {
      id: 'traderName',
      header: 'Trader',
      accessor: row => row.traderName,
      sortable: true,
      sortValue: row => row.traderName,
      width: '180px',
    },
    {
      id: 'period',
      header: 'Period',
      accessor: row => row.period,
      sortable: true,
      sortValue: row => row.period,
      width: '140px',
    },
    {
      id: 'roi',
      header: 'ROI',
      accessor: row => (
        <span className="text-green-600 font-semibold">
          {row.roi.toFixed(2)}%
        </span>
      ),
      sortable: true,
      sortValue: row => row.roi,
      width: '100px',
    },
    {
      id: 'totalProfit',
      header: 'Total Profit',
      accessor: row => `$${row.totalProfit.toLocaleString()}`,
      sortable: true,
      sortValue: row => row.totalProfit,
      width: '140px',
    },
    {
      id: 'totalTrades',
      header: 'Total Trades',
      accessor: row => row.totalTrades,
      sortable: true,
      sortValue: row => row.totalTrades,
      width: '120px',
    },
    {
      id: 'winRate',
      header: 'Win Rate',
      accessor: row => (
        <span className="font-semibold">{row.winRate.toFixed(1)}%</span>
      ),
      sortable: true,
      sortValue: row => row.winRate,
      width: '100px',
    },
    {
      id: 'maxDrawdown',
      header: 'Max Drawdown',
      accessor: row => (
        <span className="text-red-600 font-semibold">
          {row.maxDrawdown.toFixed(2)}%
        </span>
      ),
      sortable: true,
      sortValue: row => row.maxDrawdown,
      width: '140px',
    },
    {
      id: 'sharpeRatio',
      header: 'Sharpe Ratio',
      accessor: row => (
        <span className="font-semibold">{row.sharpeRatio.toFixed(2)}</span>
      ),
      sortable: true,
      sortValue: row => row.sharpeRatio,
      width: '120px',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="Performance Analytics"
          description="Analyze copy trading strategy performance metrics"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <PerformanceCard
          title="Average ROI"
          value="32.17%"
          change="+7.2% from last month"
          changeType="positive"
          icon={TrendingUp}
          description="Across all strategies"
        />

        <PerformanceCard
          title="Total Profit"
          value="$101,859"
          change="+20% from last month"
          changeType="positive"
          icon={TrendingUp}
          description="This month"
        />

        <PerformanceCard
          title="Average Win Rate"
          value="67.6%"
          change="+4.1% from last month"
          changeType="positive"
          icon={TrendingUp}
          description="Across all strategies"
        />

        <PerformanceCard
          title="Average Sharpe Ratio"
          value="2.33"
          change="+0.5 from last month"
          changeType="positive"
          icon={TrendingUp}
          description="Risk-adjusted returns"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <ROIWidget
          currentROI={42.5}
          targetROI={50}
          period="This Month"
          showTarget={true}
        />
        <ROIWidget
          currentROI={35.8}
          targetROI={45}
          period="This Quarter"
          showTarget={true}
        />
        <ROIWidget
          currentROI={18.2}
          targetROI={25}
          period="This Year"
          showTarget={true}
        />
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Performance Metrics</CardTitle>
              <CardDescription>
                Detailed performance breakdown by trader
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm">
                <Calendar className="h-4 w-4 mr-2" />
                Date Range
              </Button>
              <Button variant="outline" size="sm">
                <Filter className="h-4 w-4 mr-2" />
                Filters
              </Button>
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={performanceData}
            searchPlaceholder="Search by trader..."
            showColumnVisibility={true}
            showExportButton={false}
            showAddButton={false}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default CopyTradingPerformance;
